import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { CalendarBlank, ChartBar, ArrowsClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

// Driver-facing business overview (NOT accounting). Two windows: today and
// this month. Revenue is the driver's net fare. Paid/unpaid distinction lives
// in "Mes paiements" — this view stays simple on purpose.
export default function DriverStats() {
  const [data, setData] = useState({ today: { revenue: 0, rides: 0 }, this_month: { revenue: 0, rides: 0 } });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/driver/stats");
      setData(data);
    } catch {
      // keep prev
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div data-testid="driver-stats-section" className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-zinc-500">Vue rapide de votre activité — courses terminées uniquement.</p>
        <Button data-testid="stats-refresh" variant="outline" size="sm" onClick={fetchData}
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-8 px-2 text-xs">
          <ArrowsClockwise size={12} className="mr-1" /> Actualiser
        </Button>
      </div>

      <StatCard
        testid="stats-today"
        label="Aujourd'hui"
        revenue={data.today?.revenue || 0}
        rides={data.today?.rides || 0}
        Icon={CalendarBlank}
        color="amber"
        loading={loading}
      />

      <StatCard
        testid="stats-month"
        label="Ce mois-ci"
        revenue={data.this_month?.revenue || 0}
        rides={data.this_month?.rides || 0}
        Icon={ChartBar}
        color="emerald"
        loading={loading}
      />

      <p className="text-[11px] text-zinc-600 text-center pt-1">
        Le chiffre d'affaires (CA) correspond au tarif chauffeur — il ne tient pas compte du statut de paiement.
      </p>
    </div>
  );
}

function StatCard({ label, revenue, rides, Icon, color, loading, testid }) {
  const colors = {
    amber:   { bg: "bg-amber-500/10",  border: "border-amber-500/40",  text: "text-amber-400",  iconBg: "bg-amber-500/20" },
    emerald: { bg: "bg-emerald-500/10",border: "border-emerald-500/40",text: "text-emerald-400",iconBg: "bg-emerald-500/20" },
  }[color] || {};
  return (
    <div data-testid={testid} className={`rounded-lg border p-5 ${colors.bg} ${colors.border} ${loading ? "animate-pulse" : ""}`}>
      <div className="flex items-start gap-3">
        <span className={`p-2.5 rounded-md ${colors.iconBg}`}>
          <Icon size={20} weight="bold" className={colors.text} />
        </span>
        <div className="flex-1">
          <p className={`text-xs uppercase tracking-wider font-semibold ${colors.text}`}>{label}</p>
          <div className="mt-2 grid grid-cols-2 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">CA</p>
              <p data-testid={`${testid}-revenue`} className="text-2xl font-bold text-white tabular-nums leading-tight">
                {revenue.toFixed(2)} <span className="text-base text-zinc-400 font-medium">EUR</span>
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Courses</p>
              <p data-testid={`${testid}-rides`} className="text-2xl font-bold text-white tabular-nums leading-tight">{rides}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
